import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Layout, MenuProps, notification, Typography } from 'antd'
import { colorText } from 'src/app/themes/primaryThemeDefault'
import { CopyIcon, PenIcon } from 'src/assets/icons'
import { menuUrls } from 'src/app/shared/constants/menu.constants'

import { useBankUserSetup } from './useBankUserSetup'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { YesNoTag, StatusTag } from '../components/TagComponents'
import { getBankUserList } from '../../../../shared/services/BankUserService'
import dayjs from 'dayjs'
import { FORMAT_DATE_TIME } from '../../../../shared/constants/app.constants'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import './index.scss'
import { ShortenListTags } from 'src/app/shared/components/common/tag/ShortenListTags'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { IGetExportData } from 'src/app/shared/components/common/new-table/table-setting.model'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { useBankUserSetupFilters } from '../BankUserSetupFiltersContext'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'

const BankUserSetup: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location
  const { userPermissions } = useRolePermission()
  const [api, contextHolder] = notification.useNotification()
  const { saveSearchParams, restoreSearchParams } = useBankUserSetupFilters()

  const [defaultParams] = useState(() => {
    const restoredParams = restoreSearchParams()
    return restoredParams || null
  })

  useEffect(() => {
    if (state?.bankUserSetup?.message) {
      api.success({
        message: <RichTextField richText={state?.bankUserSetup?.message} />,
        closeIcon: null,
      })
    }
    window.history.replaceState({}, '')
  }, [state?.bankUserSetup])

  const { bankUserList, totalRecords, isLoading, getBankUserListData } = useBankUserSetup()

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>{t(`BankSetup:list.label.editBankUser`)}</Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.editBankUserSetup}/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t(`BankSetup:list.label.copyBankUser`)}
                </Typography.Text>
              ),
              icon: <CopyIcon />,
              onClick: () => {
                if (rowData?.id) {
                  navigate(`${menuUrls.copyBankUserSetup}/${rowData?.id}`)
                }
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps

    [userPermissions, t, navigate],
  )

  const generateCustomColumnDefs = useCallback(
    (columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> =>
      columnDefs.map((item, index) => {
        const { colId } = item
        if (colId === 'status')
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return <StatusTag status={data.status} />
            },
          } as any

        if (colId === 'doNotSendEmailsToThisAddress')
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return <YesNoTag status={data.doNotSendEmailsToThisAddress} />
            },
          } as any

        if (colId === 'clients') {
          return {
            ...item,
            width: 150,
            headerClass: 'custom-header-text',
            cellRenderer: (props: any) => {
              const { data } = props
              const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()

              return data.clients ? <ShortenListTags data={data.clients} width={columnWidth} /> : null
            },
          } as any
        }

        if (colId === 'userRoles') {
          return {
            ...item,
            width: 150,
            headerClass: 'custom-header-text',
            cellRenderer: (props: any) => {
              const { data } = props
              const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
              return data.userRoles ? <ShortenListTags data={data.userRoles} width={columnWidth} /> : null
            },
          } as any
        }

        if (colId === 'lastLoggedOn')
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return data.lastLoggedOn ? dayjs(data.lastLoggedOn).format(FORMAT_DATE_TIME) : ''
            },
          }

        if (['primaryPhoneNumber', 'mobileNumber'].includes(colId)) {
          item.dataType = 'number'
        }

        return item
      }),
    [],
  )

  const handleAddRecord = () => {
    navigate(menuUrls.addBankUserSetup)
  }

  const handleSearch = useCallback(async (payload: Partial<IGetExportData>) => {
    const paramsToSave = {
      ...payload,
      isSearchAllColumns: (payload.searchColumns?.length || 0) > 1,
    }
    saveSearchParams(paramsToSave)
    return await getBankUserListData(payload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveSearchParams])

  // Restore saved search params when component mounts
  useEffect(() => {
    const savedParams = restoreSearchParams()
    
    if (savedParams) {
      const { isSearchAllColumns, searchTerm } = savedParams
      setTimeout(() => {
        const formSearchColumns = isSearchAllColumns ? 'All' : (savedParams.searchColumns?.[0] || 'All')
        
        outerTableRef.current?.setSearchFormValues?.(
          searchTerm || '',
          formSearchColumns
        )
        if (searchTerm) {
          outerTableRef.current?.setSearchOpen?.(true)
        }
      }, 300)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout.Content style={{ padding: 24 }} className={'landing-bank-user-setup'}>
      {contextHolder}

      <NewCommonTable
        ref={outerTableRef}
        rowData={bankUserList}
        total={totalRecords}
        translationNamespace='BankSetup:list.tableColumns'
        displayAddBtn={userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
        displayActionColumn={userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
        loading={isLoading}
        pageName={TABLE_NAME.BANK_USER_SETUP}
        tableSettingName={TABLE_NAME.BANK_USER_SETUP}
        handleAddRecord={handleAddRecord}
        actionDropdownItems={getItems}
        handleSearch={handleSearch}
        handleDownload={getBankUserList}
        generateCustomColumnDefs={generateCustomColumnDefs}
        defaultParams={defaultParams}
      />
    </Layout.Content>
  )
}

export default BankUserSetup
